/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {cloneDeep, isPlainObject, merge, mergeWith, omitBy} from 'lodash';

import {optionDecorators} from '@amblique/base/app/helpers/option-decorators';

/**
 * Generic Plugin Options Resolver
 *
 * Pure JavaScript logic for resolving plugin options from multiple sources.
 * Object-based pattern allowing method overriding in downstream packages.
 *
 * Usage in React: Use via `usePluginOptions` hook
 * Usage in Node/SSR: Use directly
 *
 * Override example:
 * ```javascript
 * import { optionsResolver } from '@amblique/base/app/helpers/options-resolver';
 *
 * optionsResolver.resolvePreset = function(container, name, params) {
 *   // Custom logic
 * };
 * ```
 */
export const optionsResolver = {
  /**
   * JSON detection regex
   * @readonly
   */
  JSON_REGEX: /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,

  /**
   * Debug mode flag
   * @type {boolean}
   */
  debug: true,

  /**
   * Registered decorators
   * Can be extended by downstream packages
   * @type {Object}
   */
  decorators: {...optionDecorators},

  /**
   * Enable/disable debug mode
   *
   * @param {boolean} enabled - Enable debug logging
   * @returns {void}
   */
  setDebug(enabled) {
    this.debug = enabled;
  },

  /**
   * Log debug message
   *
   * @param {string} message - Debug message
   * @param {any} [data] - Optional data to log
   * @returns {void}
   */
  log(message, data) {
    if (this.debug) {
      if (data !== undefined) {
        console.log(`[Options Resolver] ${message}`, data);
      } else {
        console.log(`[Options Resolver] ${message}`);
      }
    }
  },

  /**
   * Register a new decorator
   *
   * @param {string} name - Decorator name (without @)
   * @param {Function} handler - Decorator function
   * @returns {void}
   */
  registerDecorator(name, handler) {
    this.decorators[name] = handler;
    this.log(`Registered decorator: @${name}`);
  },

  /**
   * Resolve plugin options from multiple sources
   *
   * Sources are processed in order and merged:
   * 1. Array sources: each item resolved and merged
   * 2. String sources: preset name, JSON, query string, or comma-separated
   * 3. Object sources: used directly
   *
   * @param {object} definedOptions - Options container with presets
   * @param {any|any[]} options - Option sources to resolve
   * @param {object} params - Context parameters (theme, element, etc.)
   * @returns {object} Resolved options
   */
  resolveOptions(definedOptions, options, params = {}) {
    if (Array.isArray(options)) {
      return options
        .filter((option) => !!option)
        .reduce((accumulated, entryOptions) => {
          return merge(accumulated, this.resolveOptions(definedOptions, entryOptions, params));
        }, {});
    }

    if (typeof options === 'string') {
      return this.resolveStringOptions(definedOptions, options, params);
    }

    return isPlainObject(options) ? cloneDeep(options) : {};
  },

  /**
   * Resolve string-based options
   * Handles: JSON, query strings, comma-separated presets, single preset
   *
   * @param {object} definedOptions - Options container
   * @param {string} options - String options
   * @param {object} params - Context parameters
   * @returns {object} Resolved options
   */
  resolveStringOptions(definedOptions, options, params) {
    options = options.trim();

    // Try parse as JSON: {"loop": true}
    if (this.isJSON(options)) {
      try {
        return this.resolveOptions(definedOptions, JSON.parse(options), params);
      } catch (error) {
        console.warn('[Options Resolver] Failed to parse JSON:', options);
      }
    }

    // Parse query string: "readonly?loop=true&mute=false"
    if (options.includes('?')) {
      return this.resolveQueryString(definedOptions, options, params);
    }

    // Parse comma-separated: "readonly,productTile"
    if (options.indexOf(',') !== -1) {
      return this.resolveOptions(
        definedOptions,
        options.split(',').map((s) => s.trim()),
        params
      );
    }

    // Single preset name: "readonly"
    return this.resolvePreset(definedOptions, options, params);
  },

  /**
   * Resolve single preset by name
   * Safe to override for custom preset resolution
   *
   * @param {object} definedOptions - Options container
   * @param {string} presetName - Preset name
   * @param {object} params - Context parameters
   * @returns {object} Preset options
   */
  resolvePreset(definedOptions, presetName, params) {
    if (Object.hasOwn(definedOptions, presetName)) {
      let options = definedOptions[presetName];

      // If function, call with params
      if (typeof options === 'function') {
        options = options.call(definedOptions, params);
      } else {
        options = cloneDeep(options || {});
      }

      return options;
    }

    return {};
  },

  /**
   * Resolve query string format
   * Format: "presetName?param1=value1&param2=value2"
   *
   * @param {object} definedOptions - Options container
   * @param {string} options - Query string
   * @param {object} params - Context parameters
   * @returns {object} Resolved options
   */
  resolveQueryString(definedOptions, options, params) {
    const index = options.indexOf('?');
    const presetName = options.substr(0, index);
    const queryString = options.substring(index + 1);
    const queryParams = Object.fromEntries(new URLSearchParams(queryString));

    return this.resolveOptions(
      definedOptions,
      [presetName, queryParams],
      params
    );
  },

  /**
   * Check if string is JSON format
   *
   * @param {string} str - String to check
   * @returns {boolean} True if JSON format
   */
  isJSON(str) {
    return this.JSON_REGEX.test(str);
  },

  /**
   * Merge local and server options containers
   *
   * Server options override local options, but:
   * - Function presets are called before merging
   * - Deep merge is applied
   * - Decorators are processed
   *
   * @param {object} localOptions - Local options container
   * @param {object} serverOptions - Server options container
   * @param {object} params - Context parameters
   * @returns {object} Merged options container
   */
  mergeOptions(localOptions, serverOptions, params = {}) {
    if (!serverOptions) {
      return localOptions;
    }

    const merged = mergeWith(
      cloneDeep(localOptions),
      serverOptions,
      (localOption, serverOption) => {
        // If local is a function, call it first
        if (typeof localOption === 'function') {
          localOption = localOption.call(localOptions, params);
        }

        // Deep merge
        return merge(cloneDeep(localOption), serverOption);
      }
    );

    // Apply decorators
    return this.decorate(merged, serverOptions);
  },

  /**
   * Process decorator options (keys starting with @)
   *
   * Decorators allow server-side configuration to modify behavior:
   * - @override: Override specific properties
   * - @merge: Deep merge specific properties
   * - @extend: Extend arrays
   * - @remove: Remove properties
   * - @default: Set default values
   * - Custom decorators can be added via registerDecorator()
   *
   * @param {object} localOptions - Local options
   * @param {object} serverOptions - Server options with decorators
   * @returns {object} Decorated options
   */
  decorate(localOptions, serverOptions) {
    for (const [property, configs] of Object.entries(serverOptions)) {
      if (property[0] === '@') {
        const decoratorName = property.substring(1);
        const decorator = this.decorators[decoratorName];

        if (decorator) {
          this.log(`Applying decorator @${decoratorName}`, configs);
          localOptions = decorator.call(this, localOptions, serverOptions, configs);
        } else {
          console.warn(`[Options Resolver] Unknown decorator: @${decoratorName}`);
        }

        delete localOptions[property];
      } else if (isPlainObject(configs)) {
        localOptions[property] = this.decorate(localOptions[property] || {}, configs);
      }
    }

    // Remove any remaining decorator properties
    return omitBy(localOptions, (value, key) => key[0] === '@');
  },
};
